import React from 'react';

import theme from '@src/theme';
import { ConnectorRangeWidth } from '@src/types/vaults/vault.types';

import { toBN } from '@utils/formaters.utils';

import AiIcon from '@assets/vaults/manageLiquidity/ai.svg?url';

import { PercentageLimitRange } from '@app-types/vaults/priceRange.types';

export const MAX_FARM_PERCENT = 100;

export const MIN_FARM_PERCENT = 0;

export enum PriceImpactLevel {
  Zero,
  Low,
  Medium,
  High,
}

export const PRICE_IMPACT_LEVEL_VALUE = {
  [PriceImpactLevel.Zero]: toBN(0),
  [PriceImpactLevel.Medium]: toBN(3),
  [PriceImpactLevel.High]: toBN(5),
};

export const PRICE_IMPACT_COLOR = {
  [PriceImpactLevel.Zero]: theme.primary.p3,
  [PriceImpactLevel.Low]: theme.accent.a1,
  [PriceImpactLevel.Medium]: theme.accent.a4,
  [PriceImpactLevel.High]: theme.accent.a6,
};

export enum VaultTagType {
  SPARK_SWAP = 'sparkswap',
}

export enum PersonalPerformance {
  Deposit = 'deposit',
  Now = 'now',
  Yield = 'yieldValue',
  Profit = 'profitValue',
  Pnl = 'pnl',
}

export enum PerformanceTab {
  Total = 'total',
  Personal = 'personal',
}

export const PERFORMANCE_TAB_OPTIONS = [
  {
    label: 'Total',
    value: PerformanceTab.Total,
  },
  {
    label: 'Personal',
    value: PerformanceTab.Personal,
  },
];

export const PERSONAL_PERFORMANCE_STATS = [
  {
    title: 'At deposit',
    keyValue: PersonalPerformance.Deposit,
    tooltipContent:
      "Shows deposit and withdrawal information for the vault's base token, with base tokens added or subtracted for all transactions " +
      "and $USD values measured at the deposit market price, while withdrawals follow a 'first in first out' method.",
  },
  {
    title: 'Now',
    keyValue: PersonalPerformance.Now,
    tooltipContent:
      'Shows the current net quantity and $USD value of base tokens, reflecting market price which includes Yield quantity.',
  },
  {
    title: 'Yield',
    keyValue: PersonalPerformance.Yield,
    keyValueSub: 'yieldSub',
    tooltipContent:
      'Shows auto-compounding generated yield quantity in $USD value reflecting the current market price, ' +
      'and a percentage indicating yield as a proportion of At Deposit quantity.',
  },
  {
    title: 'Profit',
    keyValue: PersonalPerformance.Profit,
    keyValueSub: 'profitSub',
    tooltipContent:
      'Shows cumulative profit generated from harvests calculated at current market price in $USD value at the time of mint.',
  },
  {
    title: 'PNL',
    keyValue: PersonalPerformance.Pnl,
    keyValueSub: 'pnlSub',
    tooltipContent:
      'Shows profit/loss, measured by the difference between Now and At Deposit $USD values, including ' +
      'Yield but not price appreciation alone, and expressed as a percentage of At Deposit quantity like Yield.',
  },
];

export enum RiskLevel {
  VERY_LOW = 1, // (Stable/stable)
  LOW = 2, // (stable/blue chip)
  MEDIUM = 3, // (blue chip/blue chip)
  HIGH = 4, // (Alt/Alt)
  VERY_HIGH = 5, // (shitcoins)
}
export const VAULT_CARD_STATS = [
  {
    title: 'Compound / Profit',
    keyValue: 'compoundProfit',
  },
  {
    title: 'Profit token',
    keyValue: 'profitToken',
  },
  // {
  //   title: 'Profit / Harvest',
  //   keyValue: 'profitHarvest',
  // },
  {
    title: 'Deposited',
    keyValue: 'deposit',
    valueColor: theme.primary.p3,
  },
  // {
  //   title: 'APY',
  //   keyValue: 'apy',
  //   valueColor: theme.accent.a1,
  // },
  // {
  //   title: 'Daily',
  //   keyValue: 'daily',
  //   valueColor: theme.accent.a1,
  // },
  // {
  //   title: 'TVL',
  //   keyValue: 'tvl',
  //   valueColor: theme.primary.p3,
  // },
  // {
  //   title: 'Liquidity',
  //   keyValue: 'liquidity',
  //   valueColor: theme.primary.p3,
  // },
  // {
  //   title: 'Risk',
  //   keyValue: 'risk',
  // },
];

export const SIMILAR_VAULT_CARD_STATS = [
  {
    title: 'Profit token',
    keyValue: 'profitToken',
  },
  {
    title: 'Compound / Profit',
    keyValue: 'compoundProfit',
  },
  {
    title: 'Liquidity',
    keyValue: 'liquidity',
    valueColor: theme.primary.p3,
  },
  {
    title: 'TVL',
    keyValue: 'tvl',
    valueColor: theme.primary.p3,
  },
  {
    title: 'APY',
    keyValue: 'apy',
    valueColor: theme.accent.a1,
  },
  {
    title: 'Daily',
    keyValue: 'daily',
    valueColor: theme.accent.a1,
  },
];

export enum VaultStats {
  Liquidity = 'liquidity',
  Deposit = 'deposit',
}

export const VAULT_STATS = [
  {
    title: 'Liquidity:',
    keyValue: VaultStats.Liquidity,
  },
  {
    title: 'Deposit:',
    keyValue: VaultStats.Deposit,
  },
];

export const REFUELING_TOOLTIP_CONTENT =
  'After each harvest the contracts claim the next emissions earned in ' +
  'order to replenish the gas used from the previous harvest. This ensures that there are always funds available to complete the next ' +
  'harvest once it becomes profitable to do so.';

export const PROFIT_VAULT_CARD_STATS = [
  {
    title: 'Compound / Profit',
    keyValue: 'compoundProfit',
  },
  {
    title: 'Available to claim',
    keyValue: 'availableToClaim',
  },
];

export enum ActionsTab {
  Deposit = 'deposit',
  Withdraw = 'withdraw',
}

export const DEFAULT_SLIDER_MARKS = [
  {
    value: 1,
    label: '1%',
  },
  {
    value: 25,
    label: '25%',
  },
  {
    value: 50,
    label: '50%',
  },
  {
    value: 75,
    label: '75%',
  },
  {
    value: 100,
    label: '100%',
  },
];

export const MIN_SLIDER_VALUE = 0;

export const MAX_SLIDER_VALUE = 100;

export enum InternalTab {
  Total = 'total',
  Personal = 'personal',
}

export const INTERNAL_TAB_OPTIONS = [
  {
    label: 'Total',
    value: InternalTab.Total,
  },
  {
    label: 'Personal',
    value: InternalTab.Personal,
  },
];

export enum ActionHistoryKeys {
  Type = 'type',
  Amount = 'amount',
  Date = 'date',
  Link = 'link',
}

export const ACTION_HISTORY_HEADER = {
  [ActionHistoryKeys.Type]: 'Type',
  [ActionHistoryKeys.Amount]: 'Amount',
  [ActionHistoryKeys.Date]: 'Date',
  [ActionHistoryKeys.Link]: 'Link',
};

export enum ActionPendingRewardsKeys {
  Token = 'token',
  Balance = 'balance',
  AccountBalance = 'accountBalance',
  Value = 'value',
  AccountValue = 'accountValue',
}

export const ACTION_PENDING_REWARDS_HEADER = {
  [ActionPendingRewardsKeys.Token]: 'Token',
  [ActionPendingRewardsKeys.Balance]: 'Balance',
  [ActionPendingRewardsKeys.AccountBalance]: 'Balance',
  [ActionPendingRewardsKeys.Value]: 'Value',
  [ActionPendingRewardsKeys.AccountValue]: 'Value',
};

export enum CreateVaultsKeys {
  Farm = 'farm',
  Exchange = 'exchange',
  Liquidity = 'liquidity',
  APR = 'apr',
  Risk = 'risk',
  Allocation = 'allocation',
  Action = 'action',
}

export const CREATE_VAULT_HEADER = {
  [CreateVaultsKeys.Farm]: 'Farm',
  [CreateVaultsKeys.Exchange]: 'Exchange',
  [CreateVaultsKeys.Liquidity]: 'Liquidity',
  [CreateVaultsKeys.APR]: 'APR',
  [CreateVaultsKeys.Risk]: 'Risk',
  [CreateVaultsKeys.Allocation]: 'Allocation',
  [CreateVaultsKeys.Action]: '',
};

export enum ChangeTakeProfitToken {
  Name = 'name',
  TVL = 'tvl',
  Price = 'price',
  Action = 'action',
}

export const CHANGE_TAKE_PROFIT_HEADER = {
  [ChangeTakeProfitToken.Name]: 'Name',
  [ChangeTakeProfitToken.TVL]: 'TVL',
  [ChangeTakeProfitToken.Price]: 'Price',
  [ChangeTakeProfitToken.Action]: '',
};

export const ZERO_PERCENT = toBN(0);

export const MIN_LIMIT_RANGE = toBN(-100);

export const MAX_LIMIT_RANGE = toBN(10000);

export const PERCENTAGE_LIMIT_RANGE: PercentageLimitRange = {
  initialMin: {
    min: MIN_LIMIT_RANGE,
    max: ZERO_PERCENT,
  },
  initialMax: {
    min: ZERO_PERCENT,
    max: MAX_LIMIT_RANGE,
  },
};

export enum FeeAmount {
  LOWEST = 100,
  LOW = 500,
  LOW_HIGH = 20000,
  MEDIUM = 2500,
  MEDIUM_BELOW = 2700,
  MEDIUM_HIGH = 3000,
  NEAR_HIGH = 6000,
  TRANSITION_HIGH = 7000,
  BELOW_HIGH = 8000,
  PRE_HIGH = 9000,
  HIGH = 10000,
}

export enum RebalanceStatus {
  Fixed = 'fixed',
  Active = 'active',
  Paused = 'paused',
}

export const REBALANCE_STATUS_TITLE = {
  [RebalanceStatus.Fixed]: 'Fixed',
  [RebalanceStatus.Active]: 'Active',
  [RebalanceStatus.Paused]: 'Paused',
};

export enum RangeTab {
  Manual = 'Manual (ALM)',
  Automatic = 'Automatic (AILM)',
}

export const RANGE_TABs = [
  {
    label: 'Manual (ALM)',
    value: RangeTab.Manual,
  },
  {
    label: 'Automatic (AILM)',
    value: RangeTab.Automatic,
    icon: <AiIcon />,
  },
];

export enum ConnectorRangeWidthType {
  Common = 'common',
  Safe = 'safe',
  Full = 'full',
}

export enum PairType {
  Stable,
  Volatile,
}

export enum FarmTag {
  Correlated = 'correlated',
  Stable = 'stable',
  BlueChip = 'bluechip',
  Volatile = 'volatile',
  EthBtc = 'etcbtc',
  Default = 'default',
}

export const QUICK_ACTIONS_CONFIG = {
  [FarmTag.Stable]: {
    [ConnectorRangeWidthType.Common]: {
      low: '10000000000000000',
      up: '10000000000000000',
    },
    [ConnectorRangeWidthType.Safe]: {
      low: '20000000000000000',
      up: '20000000000000000',
    },
    [ConnectorRangeWidthType.Full]: {
      low: '1000000000000000000',
      up: '100000000000000000000',
    },
  },
  [FarmTag.Correlated]: {
    [ConnectorRangeWidthType.Common]: {
      low: '30000000000000000',
      up: '40000000000000000',
    },
    [ConnectorRangeWidthType.Safe]: {
      low: '60000000000000000',
      up: '70000000000000000',
    },
    [ConnectorRangeWidthType.Full]: {
      low: '1000000000000000000',
      up: '100000000000000000000',
    },
  },
  [FarmTag.EthBtc]: {
    [ConnectorRangeWidthType.Common]: {
      low: '80000000000000000',
      up: '100000000000000000',
    },
    [ConnectorRangeWidthType.Safe]: {
      low: '150000000000000000',
      up: '200000000000000000',
    },
    [ConnectorRangeWidthType.Full]: {
      low: '1000000000000000000',
      up: '100000000000000000000',
    },
  },
  [FarmTag.BlueChip]: {
    [ConnectorRangeWidthType.Common]: {
      low: '200000000000000000',
      up: '250000000000000000',
    },
    [ConnectorRangeWidthType.Safe]: {
      low: '300000000000000000',
      up: '350000000000000000',
    },
    [ConnectorRangeWidthType.Full]: {
      low: '1000000000000000000',
      up: '100000000000000000000',
    },
  },
  [FarmTag.Volatile]: {
    [ConnectorRangeWidthType.Common]: {
      low: '300000000000000000',
      up: '350000000000000000',
    },
    [ConnectorRangeWidthType.Safe]: {
      low: '500000000000000000',
      up: '600000000000000000',
    },
    [ConnectorRangeWidthType.Full]: {
      low: '1000000000000000000',
      up: '100000000000000000000',
    },
  },
  [FarmTag.Default]: {
    [ConnectorRangeWidthType.Common]: {
      low: '130000000000000000',
      up: '150000000000000000',
    },
    [ConnectorRangeWidthType.Safe]: {
      low: '200000000000000000',
      up: '250000000000000000',
    },
    [ConnectorRangeWidthType.Full]: {
      low: '1000000000000000000',
      up: '100000000000000000000',
    },
  },
};

export const QUICK_ACTIONS_TITLE = {
  [ConnectorRangeWidthType.Full]: 'Full',
  [ConnectorRangeWidthType.Safe]: 'Safe',
  [ConnectorRangeWidthType.Common]: 'Common',
};

export const FARM_TAG_TITLE = {
  [FarmTag.Correlated]: 'Correlated',
  [FarmTag.Stable]: 'Stable',
  [FarmTag.BlueChip]: 'BlueChip',
  [FarmTag.Volatile]: 'Volatile',
  [FarmTag.EthBtc]: 'EthBtc',
  [FarmTag.Default]: 'Default',
};

export enum ChartType {
  PositionRange = 'positionRange',
  Apy = 'apy',
  Tvl = 'tvl',
  Price = 'price',
  Deposit = 'deposit',
  Pnl = 'pnl',
  Yield = 'yield',
}

export const LIMITED_CHART_OPTIONS = [
  {
    label: 'APY',
    value: ChartType.Apy,
  },
  {
    label: 'TVL',
    value: ChartType.Tvl,
  },
  {
    label: 'Price',
    value: ChartType.Price,
  },
];

export const FULL_CHART_OPTIONS = [
  {
    label: 'Position Range',
    value: ChartType.PositionRange,
  },
  ...LIMITED_CHART_OPTIONS,
];

export const LIMITED_PERSONAL_CHART_OPTIONS = [
  {
    label: 'Deposit',
    value: ChartType.Deposit,
  },
  {
    label: 'PNL',
    value: ChartType.Pnl,
  },
];

export const FULL_PERSONAL_CHART_OPTIONS = [
  ...LIMITED_PERSONAL_CHART_OPTIONS,
  {
    label: 'Yield',
    value: ChartType.Yield,
  },
];

export const QUICK_CONNECTOR_TYPE = [
  ConnectorRangeWidth.Safe,
  ConnectorRangeWidth.Full,
  ConnectorRangeWidth.Common,
];
